<template>
   <div class="card">
      <div class="card-body m-2">
         <div class="row p-2 align-items-center">
            <div class="col-12 d-flex flex-wrap align-items-center mb-2">
               <div class="me-auto d-flex align-items-center">
                  <button type="button" class="fw-bold btn btn-sm text-light"
                     style="font-size: 13px; height: 36px; border-radius: 8px !important; padding: 0 1.2rem; white-space: nowrap; background: #00A0C1;"
                     @click="getCronJobs()" :disabled="loading">
                     <i class="fa-solid fa-arrows-rotate me-1" :class="{ 'fa-spin': loading }"></i> Refresh
                  </button>
               </div>
            </div>
         </div>

         <!-- Table -->
         <div class="row p-2">
            <table class="table align-middle mainTable">
               <thead class="align-middle">
                  <tr>
                     <th scope="col">Job Name</th>
                     <th scope="col">Job ID</th>
                     <th scope="col">Description</th>
                     <th scope="col">Schedule</th>
                     <th scope="col" style="width: 100px;">Auto Run</th>
                     <th scope="col" style="width: 160px;">Last Executed</th>
                     <th scope="col" style="width: 120px;">Last Status</th>
                     <th scope="col" style="width: 140px;">Actions</th>
                  </tr>
               </thead>

               <tbody v-if="cronJobs.length <= 0 && !loading">
                  <tr>
                     <td colspan="8">No cron jobs found.</td>
                  </tr>
               </tbody>

               <tbody v-if="loading">
                  <tr>
                     <td colspan="8" class="text-center">
                        <i class="fa-solid fa-spinner fa-spin me-1"></i> Loading...
                     </td>
                  </tr>
               </tbody>

               <tbody v-else>
                  <tr v-for="(job, index) in cronJobs" :key="index">
                     <td style="font-weight: 600; font-size: 13px; color: #1e293b;">
                        {{ job.job_name || job.job_id }}
                     </td>
                     <td>
                        <span class="job-id-badge">{{ job.job_id }}</span>
                     </td>
                     <td style="font-size: 13px; color: #64748b;">{{ job.job_description }}</td>
                     <td>
                        <span class="schedule-pill">
                           <i class="fa-regular fa-calendar-alt me-1"></i>
                           {{ getReadableSchedule(job.schedule) }}
                        </span>
                     </td>
                     <td>
                        <div class="form-check form-switch">
                           <input class="form-check-input custom-switch" type="checkbox"
                              :checked="job.is_auto_run === 1"
                              @change="toggleAutoRun(job)"
                              :disabled="togglingJob === job.job_id || job.job_id === 'reset_expired_category_cache' || job.job_id === 'flush_tables_and_populate'">
                        </div>
                     </td>
                     <td style="font-size: 12px;">
                        <template v-if="job.last_executed_at">
                           <div>{{ formatDate(job.last_executed_at) }}</div>
                           <small class="text-muted">by {{ job.last_executed_by || 'Unknown' }}</small>
                        </template>
                        <span v-else class="text-muted" style="font-size: 12px;">Never</span>
                     </td>
                     <td>
                        <template v-if="job.last_status">
                           <span :class="getStatusClass(job.last_status)">
                              <i :class="getStatusIcon(job.last_status)" class="me-1"></i>
                              {{ capitalize(job.last_status) }}
                           </span>
                           <div v-if="job.last_status === 'failed' && job.last_error_message"
                              class="error-preview" :title="job.last_error_message">
                              {{ truncate(job.last_error_message, 40) }}
                           </div>
                        </template>
                        <span v-else class="text-muted" style="font-size: 12px;">—</span>
                     </td>
                     <td>
                        <div class="d-flex gap-1">
                           <button type="button" class="ms-action-btn ms-action-btn--edit"
                              title="Edit Schedule" @click="openEditModal(job)"
                              :disabled="job.job_id === 'reset_expired_category_cache' || job.job_id === 'flush_tables_and_populate'">
                              <i class="fa-solid fa-pen"></i>
                           </button>
                           <button type="button" class="ms-action-btn ms-action-btn--success"
                              :disabled="runningJob === job.job_id || job.job_id === 'reset_expired_category_cache' || job.job_id === 'flush_tables_and_populate'"
                              @click="confirmRunJob(job)"
                              :title="job.job_id === 'reset_expired_category_cache' || job.job_id === 'flush_tables_and_populate' ? 'Permanently Disabled' : 'Run ' + job.job_id + ' now'">
                              <i class="fa-solid fa-play" v-if="runningJob !== job.job_id"></i>
                              <i class="fa-solid fa-spinner fa-spin" v-else></i>
                           </button>
                           <button type="button" class="ms-action-btn ms-action-btn--info"
                              title="View History" @click="openHistoryModal(job)">
                              <i class="fa-solid fa-clock-rotate-left"></i>
                           </button>
                        </div>
                     </td>
                  </tr>
               </tbody>
            </table>
         </div>

      </div>
   </div>

   <!-- Edit Schedule Modal -->
   <div class="modal-backdrop-custom" v-if="showEditModal" @click.self="showEditModal = false">
      <div class="modal-content-custom">
         <button @click="showEditModal = false" class="close-button-custom">&times;</button>
         <h5 class="modal-title-custom">
            <i class="fa-solid fa-pen me-2" style="color: #00A0C1;"></i>
            Edit Cron Job
         </h5>
         <hr>
         <div class="modal-body-custom">
            <div class="mb-3">
               <label class="filter-label">Job Name</label>
               <input type="text" class="form-control form-control-sm" style="border-radius: 8px; background: #f8f9fa;"
                  :value="editJob.job_name" disabled>
            </div>
            <div class="mb-3">
               <label class="filter-label">Job ID</label>
               <input type="text" class="form-control form-control-sm" style="border-radius: 8px; background: #f8f9fa;"
                  :value="editJob.job_id" disabled>
            </div>

            <div class="row">
               <div class="col-6 mb-3">
                  <label class="filter-label">Frequency <span class="text-danger">*</span></label>
                  <select class="form-select form-select-sm" style="border-radius: 8px;" v-model="scheduleFrequency">
                     <option value="daily">Daily</option>
                     <option value="weekly">Weekly</option>
                     <option value="biweekly">Fortnightly</option>
                     <option value="monthly">Monthly</option>
                  </select>
               </div>
               <div class="col-6 mb-3" v-if="scheduleFrequency === 'weekly' || scheduleFrequency === 'biweekly'">
                  <label class="filter-label">Day of Week <span class="text-danger">*</span></label>
                  <select class="form-select form-select-sm" style="border-radius: 8px;" v-model="scheduleDay">
                     <option value="0">Sunday</option>
                     <option value="1">Monday</option>
                     <option value="2">Tuesday</option>
                     <option value="3">Wednesday</option>
                     <option value="4">Thursday</option>
                     <option value="5">Friday</option>
                     <option value="6">Saturday</option>
                  </select>
               </div>
               <div class="col-6 mb-3" v-if="scheduleFrequency === 'monthly'">
                  <label class="filter-label">Day of Month <span class="text-danger">*</span></label>
                  <select class="form-select form-select-sm" style="border-radius: 8px;" v-model="scheduleDayOfMonth">
                     <option v-for="d in 28" :value="d" :key="d">{{ d }}</option>
                  </select>
               </div>
            </div>

            <div class="mb-3">
               <label class="filter-label">Time (UTC) <span class="text-danger">*</span></label>
               <input type="time" class="form-control form-control-sm" style="border-radius: 8px;" v-model="scheduleTime">
            </div>

            <div class="schedule-preview">
               <i class="fa-solid fa-clock me-1"></i>
               <strong>Preview:</strong> {{ getReadableSchedule(generateCronString()) }}
            </div>
         </div>
         <div class="modal-footer-custom">
            <button type="button" class="btn btn-sm btn-modal-cancel" @click="showEditModal = false">Cancel</button>
            <button type="button" class="btn btn-sm text-light" style="background: #00A0C1; border-radius: 8px;"
               @click="saveSchedule()">
               <i class="fa-solid fa-floppy-disk me-1"></i> Save
            </button>
         </div>
      </div>
   </div>

   <!-- Run Confirm Modal -->
   <div class="modal-backdrop-custom" v-if="showRunModal" @click.self="showRunModal = false">
      <div class="modal-content-custom">
         <button @click="showRunModal = false" class="close-button-custom">&times;</button>
         <h5 class="modal-title-custom">
            <i class="fa-solid fa-play me-2" style="color: #00A0C1;"></i>
            Run Cron Job
         </h5>
         <hr>
         <div class="modal-body-custom">
            <p>Are you sure you want to manually trigger this cron job?</p>
            <div class="job-display">{{ selectedRunJob?.job_name || selectedRunJob?.job_id }}</div>
            <small class="text-muted mt-2 d-block">{{ selectedRunJob?.job_description }}</small>
         </div>
         <div class="modal-footer-custom">
            <button type="button" class="btn btn-sm btn-modal-cancel" @click="showRunModal = false">Cancel</button>
            <button type="button" class="btn btn-sm text-light" style="background: #00A0C1; border-radius: 8px;"
               @click="executeRunJob()">
               <i class="fa-solid fa-play me-1"></i> Run Now
            </button>
         </div>
      </div>
   </div>

   <!-- History Modal -->
   <div class="modal-backdrop-custom" v-if="showHistoryModal" @click.self="closeHistoryModal()">
      <div class="modal-content-custom modal-content-wide">
         <button @click="closeHistoryModal()" class="close-button-custom">&times;</button>
         <h5 class="modal-title-custom">
            <i class="fa-solid fa-clock-rotate-left me-2" style="color: #00A0C1;"></i>
            Execution History — {{ historyJobName }}
         </h5>
         <hr>
         <div class="modal-body-custom">
            <div v-if="historyLoading" class="text-center py-3">
               <i class="fa-solid fa-spinner fa-spin me-1"></i> Loading history...
            </div>
            <div v-else-if="!jobHistory.length" class="text-center py-3 text-muted">
               <i class="fa-solid fa-inbox me-1"></i> No execution history found.
            </div>
            <div v-else class="history-scroll">
               <table class="table align-middle mainTable mb-0">
                  <thead>
                     <tr>
                        <th>Execution Date</th>
                        <th>Executed By</th>
                        <th>Server</th>
                        <th>Status</th>
                        <th>Error</th>
                     </tr>
                  </thead>
                  <tbody>
                     <tr v-for="record in jobHistory" :key="record.id">
                        <td style="font-size: 13px;">{{ formatDate(record.executed_timestamp) }}</td>
                        <td style="font-size: 13px;">
                           <span :class="isSystemExecution(record.executed_by) ? 'executed-by-system' : 'executed-by-user'">
                              <i :class="isSystemExecution(record.executed_by) ? 'fa-solid fa-robot' : 'fa-solid fa-user'" class="me-1"></i>
                              {{ record.executed_by || 'Unknown' }}
                           </span>
                        </td>
                        <td>
                           <code class="server-badge">{{ record.executing_server }}</code>
                        </td>
                        <td>
                           <span :class="getStatusClass(record.status)">
                              <i :class="getStatusIcon(record.status)" class="me-1"></i>
                              {{ capitalize(record.status) }}
                           </span>
                        </td>
                        <td style="font-size: 12px; max-width: 200px;">
                           <span v-if="record.error_message" class="error-text" :title="record.error_message">
                              {{ truncate(record.error_message, 60) }}
                           </span>
                           <span v-else class="text-muted">—</span>
                        </td>
                     </tr>
                  </tbody>
               </table>

               <!-- History Pagination -->
               <div class="d-flex justify-content-between align-items-center mt-2" v-if="historyPagination.totalCount > historyPagination.pageSize">
                  <small class="text-muted">
                     {{ ((historyPagination.page - 1) * historyPagination.pageSize) + 1 }} - {{ Math.min(historyPagination.page * historyPagination.pageSize, historyPagination.totalCount) }}
                     of {{ historyPagination.totalCount }}
                  </small>
                  <div class="d-flex gap-1">
                     <button class="btn btn-sm btn-outline-secondary" :disabled="historyPagination.page <= 1"
                        @click="historyPagination.page--; getJobHistory()">
                        <i class="fa-solid fa-chevron-left"></i>
                     </button>
                     <button class="btn btn-sm btn-outline-secondary"
                        :disabled="historyPagination.page >= Math.ceil(historyPagination.totalCount / historyPagination.pageSize)"
                        @click="historyPagination.page++; getJobHistory()">
                        <i class="fa-solid fa-chevron-right"></i>
                     </button>
                  </div>
               </div>
            </div>
         </div>
      </div>
   </div>
</template>

<script>
import moment from 'moment'

export default {
   data() {
      return {
         cronJobs: [],
         loading: false,
         runningJob: null,
         togglingJob: null,

         // Edit modal
         showEditModal: false,
         editJob: {},
         scheduleFrequency: 'daily',
         scheduleDay: '0',
         scheduleDayOfMonth: 1,
         scheduleTime: '00:00',

         // Run modal
         showRunModal: false,
         selectedRunJob: null,

         // History modal
         showHistoryModal: false,
         historyJobId: '',
         historyJobName: '',
         historyLoading: false,
         jobHistory: [],
         historyPagination: {
            page: 1,
            pageSize: 20,
            totalCount: 0,
         },
      }
   },

   created() {
      this.getCronJobs()
   },

   methods: {
      formatDate(date) {
         return moment.utc(date).local().format("DD-MMM-YYYY, hh:mm A")
      },

      getStatusClass(status) {
         switch (status) {
            case 'success': return 'status-badge status-success'
            case 'failed': return 'status-badge status-failed'
            case 'pending': return 'status-badge status-pending'
            default: return 'status-badge status-unknown'
         }
      },

      getStatusIcon(status) {
         switch (status) {
            case 'success': return 'fa-solid fa-circle-check'
            case 'failed': return 'fa-solid fa-circle-xmark'
            case 'pending': return 'fa-solid fa-clock'
            default: return 'fa-solid fa-question-circle'
         }
      },

      capitalize(str) {
         if (!str) return '—'
         return str.charAt(0).toUpperCase() + str.slice(1)
      },

      truncate(str, maxLen) {
         if (!str) return ''
         return str.length > maxLen ? str.substring(0, maxLen) + '…' : str
      },

      isSystemExecution(executedBy) {
         return executedBy && executedBy.toLowerCase() === 'system'
      },

      // ─── Schedule Helpers ───
      getReadableSchedule(schedule) {
         if (!schedule) return '—'
         const parts = schedule.split(' ')
         if (parts.length < 6) return schedule

         const [, minutes, hours, dayOfMonth, , dayOfWeek] = parts
         const h = String(hours).padStart(2, '0')
         const m = String(minutes).padStart(2, '0')
         const timeStr = `${h}:${m} UTC`
         const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

         if (dayOfMonth === '*' && dayOfWeek === '*') {
            return `Daily at ${timeStr}`
         } else if (dayOfMonth === '*' && dayOfWeek !== '*') {
            const dayName = days[parseInt(dayOfWeek)] || dayOfWeek
            return `Weekly on ${dayName} at ${timeStr}`
         } else if (dayOfMonth !== '*' && dayOfWeek === '*') {
            return `Monthly on the ${this.ordinal(parseInt(dayOfMonth))} at ${timeStr}`
         }
         return schedule
      },

      ordinal(n) {
         const s = ['th', 'st', 'nd', 'rd']
         const v = n % 100
         return n + (s[(v - 20) % 10] || s[v] || s[0])
      },

      generateCronString() {
         const [hours, minutes] = this.scheduleTime.split(':')
         switch (this.scheduleFrequency) {
            case 'daily':
               return `0 ${minutes} ${hours} * * *`
            case 'weekly':
               return `0 ${minutes} ${hours} * * ${this.scheduleDay}`
            case 'biweekly':
               // Cron doesn't natively support biweekly, use weekly day + admin note
               return `0 ${minutes} ${hours} * * ${this.scheduleDay}`
            case 'monthly':
               return `0 ${minutes} ${hours} ${this.scheduleDayOfMonth} * *`
            default:
               return `0 ${minutes} ${hours} * * *`
         }
      },

      parseCronToForm(schedule) {
         const parts = schedule.split(' ')
         if (parts.length < 6) return

         const [, minutes, hours, dayOfMonth, , dayOfWeek] = parts
         this.scheduleTime = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`

         if (dayOfMonth === '*' && dayOfWeek === '*') {
            this.scheduleFrequency = 'daily'
         } else if (dayOfMonth === '*' && dayOfWeek !== '*') {
            this.scheduleFrequency = 'weekly'
            this.scheduleDay = dayOfWeek
         } else if (dayOfMonth !== '*' && dayOfWeek === '*') {
            this.scheduleFrequency = 'monthly'
            this.scheduleDayOfMonth = parseInt(dayOfMonth)
         } else {
            this.scheduleFrequency = 'daily'
         }
      },

      // ─── API Calls ───
      async getCronJobs() {
         this.loading = true
         try {
            const resp = await this.$axios.get("admin-controls/get-cron-jobs")
            this.cronJobs = resp.data.cron_jobs || []
         } catch (error) {
            console.error(error)
            this.$toast.error("Failed to load cron jobs.")
         } finally {
            this.loading = false
         }
      },

      async toggleAutoRun(job) {
         this.togglingJob = job.job_id
         const newValue = job.is_auto_run === 1 ? 0 : 1
         try {
            const resp = await this.$axios.post("admin-controls/toggle-cron-job", {
               job_id: job.job_id,
               is_auto_run: newValue,
            })
            if (resp.data.success) {
               job.is_auto_run = newValue
               this.$toast.success(resp.data.message)
            } else {
               this.$toast.error(resp.data.message)
            }
         } catch (error) {
            console.error(error)
            this.$toast.error("Failed to toggle cron job.")
         } finally {
            this.togglingJob = null
         }
      },

      // ─── Edit Schedule ───
      openEditModal(job) {
         this.editJob = { ...job }
         this.parseCronToForm(job.schedule)
         this.showEditModal = true
      },

      async saveSchedule() {
         const newSchedule = this.generateCronString()
         try {
            const resp = await this.$axios.post("admin-controls/update-cron-schedule", {
               job_id: this.editJob.job_id,
               schedule: newSchedule,
            })
            if (resp.data.success) {
               this.$toast.success(resp.data.message)
               this.showEditModal = false
               this.getCronJobs()
            } else {
               this.$toast.error(resp.data.message)
            }
         } catch (error) {
            console.error(error)
            this.$toast.error("Failed to update schedule.")
         }
      },

      // ─── Run Job ───
      confirmRunJob(job) {
         this.selectedRunJob = job
         this.showRunModal = true
      },

      async executeRunJob() {
         if (!this.selectedRunJob) return
         this.showRunModal = false
         this.runningJob = this.selectedRunJob.job_id

         try {
            const resp = await this.$axios.post("admin-controls/run-cron-job", {
               job_id: this.selectedRunJob.job_id,
            })
            if (resp.data.success) {
               this.$toast.success(resp.data.message)
            } else {
               this.$toast.error(resp.data.message)
            }
         } catch (error) {
            console.error(error)
            this.$toast.error("Failed to run cron job.")
         } finally {
            this.runningJob = null
            setTimeout(() => this.getCronJobs(), 3000)
         }
      },

      // ─── History ───
      openHistoryModal(job) {
         this.historyJobId = job.job_id
         this.historyJobName = job.job_name || job.job_id
         this.historyPagination.page = 1
         this.jobHistory = []
         this.showHistoryModal = true
         this.getJobHistory()
      },

      async getJobHistory() {
         this.historyLoading = true
         try {
            let apiPath = "admin-controls/get-cron-job-history?job_id=" + this.historyJobId
               + "&page=" + this.historyPagination.page
               + "&page_size=" + this.historyPagination.pageSize
            const resp = await this.$axios.get(apiPath)
            if (resp.data.success) {
               this.jobHistory = resp.data.history || []
               this.historyPagination.totalCount = resp.data.pagination?.total_count || 0
            } else {
               this.$toast.error(resp.data.message)
            }
         } catch (error) {
            console.error(error)
            this.$toast.error("Failed to load job history.")
         } finally {
            this.historyLoading = false
         }
      },

      closeHistoryModal() {
         this.showHistoryModal = false
         this.jobHistory = []
      },
   },
}
</script>

<style scoped>
.card { border: none; }

.job-id-badge {
   font-family: monospace;
   font-size: 11px;
   background: #e8f4f8;
   color: #00748a;
   padding: 3px 7px;
   border-radius: 5px;
   font-weight: 600;
   white-space: nowrap;
}

.schedule-pill {
   font-size: 12px;
   background: #f0fdf4;
   color: #15803d;
   padding: 4px 10px;
   border-radius: 6px;
   font-weight: 500;
   white-space: nowrap;
   display: inline-block;
}

.schedule-preview {
   font-size: 13px;
   background: #f0fdf4;
   color: #15803d;
   padding: 10px 14px;
   border-radius: 8px;
   border: 1px dashed #86efac;
}

.server-badge {
   font-size: 11px;
   background: #f1f5f9;
   color: #475569;
   padding: 2px 6px;
   border-radius: 4px;
}

/* Action buttons */
.btn-action {
   width: 30px; height: 30px; padding: 0;
   border-radius: 6px; display: inline-flex;
   align-items: center; justify-content: center;
   font-size: 12px; transition: all 0.15s ease;
}
.btn-action-edit { border: 1px solid #00A0C1; color: #00A0C1; background: #fff; }
.btn-action-edit:hover { background: #00A0C1; color: #fff; }
.btn-action-run { border: 1px solid #10b981; color: #10b981; background: #fff; }
.btn-action-run:hover:not(:disabled) { background: #10b981; color: #fff; }
.btn-action-run:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-action-history { border: 1px solid #6366f1; color: #6366f1; background: #fff; }
.btn-action-history:hover { background: #6366f1; color: #fff; }

.custom-switch {
   width: 2.5em;
   height: 1.25em;
   cursor: pointer;
}
.custom-switch:checked {
   background-color: #00A0C1;
   border-color: #00A0C1;
}

/* Job display in run modal */
.job-display {
   font-family: monospace;
   font-size: 13px;
   background: #e8f4f8;
   padding: 8px 12px;
   border-radius: 8px;
   color: #00748a;
   word-break: break-all;
}

/* History scroll */
.history-scroll {
   max-height: 400px;
   overflow-y: auto;
}

/* Modal */
.modal-backdrop-custom {
   position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
   background-color: rgba(0,0,0,0.5); display: flex;
   justify-content: center; align-items: center; z-index: 1050;
}
.modal-content-custom {
   position: relative; background: #fff; padding: 2rem;
   border-radius: 14px; width: 100%; max-width: 500px;
   box-shadow: 0 8px 30px rgba(0,0,0,0.2);
}
.modal-content-wide {
   max-width: 800px;
}
.modal-title-custom { font-weight: 700; font-size: 1.1rem; color: #435666; margin-bottom: 0; }
.modal-content-custom hr { margin: 0.75rem 0 1rem; border-color: #e9ecef; }
.close-button-custom {
   position: absolute; top: 0.75rem; right: 1rem;
   font-size: 1.8rem; font-weight: bold; line-height: 1;
   color: #adb5bd; background: none; border: none; cursor: pointer;
}
.close-button-custom:hover { color: #435666; }
.modal-body-custom { font-size: 14px; color: #435666; }
.filter-label {
   font-size: 11px; font-weight: 600; color: #6c757d;
   text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 4px; display: block;
}
.modal-footer-custom { margin-top: 1rem; display: flex; justify-content: flex-end; gap: 8px; }
.btn-modal-cancel { background: #e9ecef; border: none; border-radius: 8px; color: #435666; }

/* Status badges */
.status-badge {
   font-size: 11px;
   font-weight: 600;
   padding: 3px 8px;
   border-radius: 6px;
   white-space: nowrap;
   display: inline-flex;
   align-items: center;
}
.status-success {
   background: #f0fdf4;
   color: #15803d;
}
.status-failed {
   background: #fef2f2;
   color: #dc2626;
}
.status-pending {
   background: #fffbeb;
   color: #d97706;
}
.status-unknown {
   background: #f1f5f9;
   color: #64748b;
}

.error-preview {
   font-size: 11px;
   color: #dc2626;
   margin-top: 2px;
   white-space: nowrap;
   overflow: hidden;
   text-overflow: ellipsis;
   max-width: 150px;
   cursor: help;
}

.error-text {
   color: #dc2626;
   word-break: break-word;
   cursor: help;
}

.executed-by-system {
   font-size: 12px;
   color: #6366f1;
   font-weight: 500;
}
.executed-by-user {
   font-size: 12px;
   color: #0891b2;
   font-weight: 500;
}
</style>
